import React, { useState } from 'react';
import {
  Card,
  Typography,
  Button,
  Container,
  Box,
  Chip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Check,
  EmojiEvents,
  Star,
  FlashOn,
  Diamond,
  DirectionsCar,
  ConfirmationNumber,
  RocketLaunch
} from '@mui/icons-material';
import { ProfessionalPlansPricingSection } from './ProfessionalPricingSection'
import { ContactCouponModal } from './ContactCouponModal'

const plansData = [
  {
    id: "start",
    name: "WashStart",
    prices: {
      montly: {
        M: 121.41,
        G: 139.62,
        PRO: 0
      },
      annual: {
        M: 598.80,
        G: 760.68,
        PRO: 0
      }
    },
    percentageSavings: 60,
    description: "Lavagens limitadas, rede ampla",
    features: [
      "Até 2 lavagens por mês",
      "Rede ampla de parceiros",
      "Suporte prioritário",
      "Cancelamento a qualquer momento",
      "App premium"
    ],
    icon: RocketLaunch,
    popular: false,
    color: "#16a34a"
  },
  {
    id: "flex",
    name: "WashFlex",
    prices: {
      montly: {
        M: 175.41,
        G: 201.72,
        PRO: 0
      },
      annual: {
        M: 1402.92,
        G: 1613.40,
        PRO: 0
      }
    },
    percentageSavings: 30,
    description: "Lavagens ilimitadas, rede ampla",
    features: [
      "Até 3 lavagens por mês",
      "Rede ampla de parceiros",
      "Suporte prioritário",
      "Cancelamento a qualquer momento",
      "App premium"
    ],
    icon: FlashOn,
    popular: false,
    color: "#06b6d4"
  },
  {
    id: "prime",
    name: "WashPrime",
    prices: {
      montly: {
        M: 242.91,
        G: 279.35,
        PRO: 0
      },
      annual: {
        M: 1942.92,
        G: 2234.40,
        PRO: 0
      }
    },
    percentageSavings: 30,
    description: "Rede premium + benefícios extras",
    features: [
      "Até 5 lavagens por mês",
      "Rede premium de parceiros",
      "Benefícios extras",
      "Suporte 24/7",
      "Agendamento prioritário",
      "Cancelamento a qualquer momento"
    ],
    icon: Star,
    popular: false,
    color: "#3b82f6"
  },
  {
    id: "elite",
    name: "WashElite",
    prices: {
      montly: {
        M: 269.91,
        G: 310.40,
        PRO: 0
      },
      annual: {
        M: 2158.92,
        G: 2482.80,
        PRO: 0
      }
    },
    percentageSavings: 30,
    description: "Premium + exclusivos",
    features: [
      "Lavagens ilimitadas",
      "Rede premium + exclusiva de parceiros",
      "Atendimento preferencial",
      "Serviços diferenciados inclusos",
      "Benefícios adicionais",
      "Cancelamento a qualquer momento"
    ],
    icon: EmojiEvents,
    popular: true,
    color: "#8b5cf6"
  },
  {
    id: "ultra",
    name: "WashUltra",
    prices: {
      montly: {
        M: 404.91,
        G: 465.65,
        PRO: 0
      },
      annual: {
        M: 3238.92,
        G: 3724.80,
        PRO: 0
      }
    },
    percentageSavings: 30,
    description: "Experiência VIP completa",
    features: [
      "Lavagens ilimitadas",
      "Acesso à rede completa de parceiros",
      "Benefícios exclusivos da rede",
      "Experiência diferenciada",
      "Cancelamento a qualquer momento"
    ],
    icon: Diamond,
    popular: false,
    color: "linear-gradient(45deg, #fbbf24, #f59e0b)"
  }
] as const;

// Format price to Brazilian currency format
const formatPrice = (price: number) => {
  return price.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const PricingSection = () => {
  const [carType, setCarType] = useState<'M' | 'G' | 'PRO'>('M');
  const [couponModalOpen, setCouponModalOpen] = useState(false)

  if (!plansData || !Array.isArray(plansData)) {
    console.error('plansData is not defined or not an array:', plansData);
    return <div>Erro ao carregar planos</div>;
  }

  const currentPlans = plansData.map(plan => ({
    ...plan,
    price: plan.prices.montly[carType], // Preço "por" (com desconto, 50% OFF)
    originalPrice: plan.prices.montly[carType] * 2, // Preço "de" (original, 50% mais caro)
    annual: plan.prices.annual[carType]
  }));

  const handleCarTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newCarType: 'M' | 'G' | 'PRO' | null,
  ) => {
    if (newCarType === null) {
      return
    }

    setCarType(newCarType);
  };

  return (
    <section id="planos" className="section-spacing bg-background">
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }} className="component-spacing">
          <Chip
            icon={<Star />}
            label="PARCERIA EXCLUSIVA DR. MONITORA"
            sx={{
              backgroundColor: 'rgba(6, 182, 212, 0.1)',
              color: '#06b6d4',
              fontWeight: 600,
              mb: 2
            }}
          />
          <Typography
            variant="h2"
            className="text-section"
            sx={{
              color: '#1a202c',
              mb: 2,
              fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' },
              fontWeight: 700
            }}
          >
            Até 60% OFF nos planos WashPass <br />
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', maxWidth: '860px', mb: 2, mx: 'auto' }}>
            <Typography component={'span'}
              sx={{
                color: '#1a202c',
                fontSize: '1.5rem',
                fontWeight: 700,
              }}>
              Lave seu carro quantas vezes quiser pagando muito menos com seu benefício exclusivo dr.monitora
            </Typography>
          </Box>

          <Typography
            variant="h6"
            sx={{
              color: '#64748b',
              maxWidth: '800px',
              mx: 'auto',
              mb: 4
            }}
          >
            Planos flexíveis para diferentes modelos de carros. Todos incluem lavagem completa básica (externa + aspiração interna). Quanto maior o plano, mais benefícios e acesso à rede premium de parceiros qualificados.
            Utilize o cupom exclusivo da parceria para garantir seu desconto especial.
          </Typography>

          {/* Car Type Toggle */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: '#f8fafc',
            borderRadius: 2,
            p: 0.5,
            mb: 4,
            width: { xs: '100%', sm: 'auto' },
            maxWidth: { xs: '100%', sm: 'none' },
            mx: 'auto'
          }}>
            <ToggleButtonGroup
              value={carType}
              exclusive
              onChange={handleCarTypeChange}
              aria-label="tipo de carro"
              fullWidth={true}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 0.5, sm: 0 },
                '& .MuiToggleButton-root': {
                  px: { xs: 2, sm: 2, md: 3 },
                  py: { xs: 1.25, sm: 1.25, md: 1.5 },
                  fontWeight: 600,
                  border: 'none',
                  fontSize: { xs: '0.875rem', sm: '0.875rem', md: '1rem' },
                  whiteSpace: 'nowrap',
                  lineHeight: 1.5,
                  textAlign: 'center',
                  width: { xs: '100%', sm: 'auto' },
                  mb: { xs: 0.5, sm: 0 },
                  '&:last-child': {
                    mb: 0
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'white',
                    color: '#1a202c',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }
                }
              }}
            >
              <ToggleButton value="M" aria-label="carros M">
                <DirectionsCar sx={{ mr: 1, width: 16, height: 16, flexShrink: 0 }} />
                Carros M (Hatch/Sedan)
              </ToggleButton>
              <ToggleButton value="G" aria-label="carros G">
                <DirectionsCar sx={{ mr: 1, width: 16, height: 16, flexShrink: 0 }} />
                Carros G (SUV/Caminhonete)
              </ToggleButton>
              <ToggleButton value="PRO" aria-label="motoristas profissionais">
                <DirectionsCar sx={{ mr: 1, width: 16, height: 16, flexShrink: 0 }} />
                Motoristas Profissionais
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Plans Grid */}
        {['M', 'G'].includes(carType) &&
          <>
            <Box sx={{
              mb: 4,
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(5, 1fr)'
              },
              gap: '16px'
            }}>
              {currentPlans.map((plan) => {
                const Icon = plan.icon;

                const normalPriceAnnually = plan.price * 12
                const savings = normalPriceAnnually - plan.annual

                return (
                  <Box key={plan.id} sx={{ display: 'flex' }}>
                    <Card
                      sx={{
                        position: 'relative',
                        p: 3,
                        boxShadow: plan.popular
                          ? '0 8px 30px rgba(6, 182, 212, 0.15)'
                          : '0 4px 20px rgba(30, 58, 138, 0.1)',
                        background: plan.popular
                          ? 'linear-gradient(145deg, rgba(6, 182, 212, 0.05) 0%, transparent 100%)'
                          : 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                        border: plan.popular ? '2px solid #06b6d4' : '1px solid #e2e8f0',
                        borderRadius: '10px',
                        transform: plan.popular ? 'scale(1.05)' : 'scale(1)',
                        transition: 'all 0.3s ease',
                        flex: 1, // Ocupa toda a altura disponível do Grid item
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'visible', // Permite que elementos absolutos saiam do card
                        '&:hover': {
                          boxShadow: '0 8px 30px rgba(30, 58, 138, 0.15)',
                          transform: plan.popular ? 'scale(1.05) translateY(-8px)' : 'translateY(-8px)'
                        },
                        width: '100%'
                      }}
                    >
                      {/* Popular Badge */}
                      {plan.popular && (
                        <Chip
                          label="Mais Popular"
                          sx={{
                            position: 'absolute',
                            top: -12,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: '#06b6d4',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            zIndex: 10
                          }}
                        />
                      )}

                      <Box sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', pt: 2, }}>
                        {/* Icon */}
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2,
                            background: plan.color.includes('gradient')
                              ? plan.color
                              : plan.color,
                            transition: 'transform 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.1)'
                            }
                          }}
                        >
                          <Icon sx={{ color: 'white', width: 32, height: 32 }} />
                        </Box>

                        {/* Plan Name */}
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: '#1a202c',
                            mb: 1
                          }}
                        >
                          {plan.name}
                        </Typography>

                        {/* Price */}
                        <Box sx={{ mb: 2 }}>
                          {/* Mensal */}
                          <Typography
                            sx={{
                              fontSize: '0.85rem',
                              color: '#64748b',
                              mb: 0.5
                            }}
                          >
                            Plano mensal
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: '1.4rem',
                              fontWeight: 400,
                              color: '#1a202c'
                            }}
                          >
                            R$ {formatPrice(plan.price)}<span style={{ fontSize: '0.9rem', fontWeight: 400 }}> /mês</span>
                          </Typography>

                          {/* Divider */}
                          <Box sx={{
                            my: 2,
                            borderTop: '1px dashed #e2e8f0',
                            position: 'relative'
                          }}>
                            <Chip
                              label="OU ANUAL"
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: -10,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                backgroundColor: '#64748b', //'#f1f5f9',
                                color: 'white',
                                fontSize: '0.7rem'
                              }}
                            />
                          </Box>

                          {/* Anual destaque */}
                          <Box sx={{
                            background: 'linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)',
                            border: '1px solid #67e8f9',
                            borderRadius: 2,
                            px: '7px',
                            py: 2.5,
                            marginTop: '28px'
                          }}>
                            <Typography
                              sx={{
                                fontSize: '1.6rem',
                                fontWeight: 800,
                                color: '#06b6d4',
                                lineHeight: 1.2
                              }}
                            >
                              R$ {formatPrice(plan.annual / 12)}<span style={{ fontSize: '0.9rem' }}> /mês*</span>
                            </Typography>

                            {/* Economia */}
                            <Typography
                              sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: '#16a34a',
                                mt: 0.5
                              }}
                            >
                              Economize R$ {formatPrice(savings)}
                              <Typography
                                sx={{
                                  fontSize: '0.8rem',
                                  fontWeight: 600,
                                  color: '#16a34a',
                                  mt: 0.5
                                }}>
                                ({plan.percentageSavings}% OFF)
                              </Typography>
                            </Typography>
                          </Box>
                        </Box>

                        {/* Description */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#64748b',
                            mb: 3
                          }}
                        >
                          {plan.description}
                        </Typography>

                        {/* Features */}
                        <Box sx={{ mb: 3, flex: 1 }}>
                          {plan.features.map((feature, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 1.5
                              }}
                            >
                              <Check sx={{
                                color: '#06b6d4',
                                width: 16,
                                height: 16,
                                mr: 1,
                                flexShrink: 0
                              }} />
                              <Typography
                                variant="body2"
                                sx={{ color: '#1a202c', textAlign: 'left' }}
                              >
                                {feature}
                              </Typography>
                            </Box>
                          ))}
                        </Box>

                        {/* CTA Button */}
                        <Button
                          fullWidth
                          variant={plan.popular ? "contained" : "outlined"}
                          startIcon={<ConfirmationNumber />}
                          onClick={() => setCouponModalOpen(true)}
                          sx={{
                            fontWeight: 600,
                            backgroundColor: plan.popular ? '#06b6d4' : 'transparent',
                            color: plan.popular ? 'white' : '#06b6d4',
                            borderColor: '#06b6d4',
                            '&:hover': {
                              backgroundColor: plan.popular ? '#0891b2' : '#06b6d4',
                              color: 'white'
                            },
                            transition: 'all 0.3s ease'
                          }}
                        >
                          Resgatar Cupom
                        </Button>
                      </Box>
                    </Card>
                  </Box>
                );
              })}
            </Box>

            {/* Footer Note */}
            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  maxWidth: '600px',
                  mx: 'auto'
                }}
              >
                <span style={{ fontWeight: 700 }}>* Valor cobrado no plano anual com parcela única.</span>
              </Typography>
            </Box>
          </>
        }

        {carType === 'PRO' && <ProfessionalPlansPricingSection onOpenCouponModal={() => setCouponModalOpen(true)} />}

        <ContactCouponModal isOpen={couponModalOpen} onClose={() => setCouponModalOpen(false)} />
      </Container>
    </section>
  );
};
